// Zero-Lag Highlighting Engine

let allSpansArray = [];
let prevSelected = [];

/**
 * Recursively wraps text nodes of an element into individual span elements
 * with the class name 'custom-char'.
 * @param {HTMLElement} element - The target DOM element
 */
export function wrapTextNodes(element) {
    if (!element) return;
    const nodes = Array.from(element.childNodes);
    nodes.forEach(node => {
        if (node.nodeType === 3) { // Node.TEXT_NODE
            const text = node.nodeValue;
            if (text.trim() === '' && text.length === 1) return;
            
            const fragment = document.createDocumentFragment();
            for (let i = 0; i < text.length; i++) {
                const char = text[i];
                const span = document.createElement('span');
                span.className = 'custom-char';
                span.textContent = char;
                fragment.appendChild(span);
            }
            node.replaceWith(fragment);
        } else if (node.nodeType === 1) { // Node.ELEMENT_NODE
            if (!['SCRIPT', 'STYLE', 'IFRAME', 'I', 'svg', 'path'].includes(node.tagName)) {
                wrapTextNodes(node);
            }
        }
    });
}

/**
 * Injects the CSS highlighting styles into the document head,
 * exposing custom properties for theme configuration.
 */
export function injectStyles() {
    if (typeof document === 'undefined') return;
    const styleId = 'ink-highlight-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
        .custom-char {
            position: relative;
            display: inline;
        }
        .custom-char.selected {
            color: var(--ink-text-color, #fffcf2) !important;
            background-color: transparent !important;
        }
        .custom-char.selected::before {
            content: '';
            position: absolute;
            left: var(--ink-x-offset, 0);
            right: var(--ink-x-offset, 0);
            top: var(--ink-y-offset, 50%);
            transform: translateY(-50%);
            height: var(--ink-height, 0.8em);
            background-color: var(--ink-color, #000000);
            z-index: -1;
        }
    `;
    document.head.appendChild(style);
}

/**
 * Initializes the zero-lag selection engine on target elements.
 * @param {string[]} targetSelectors - Array of query selectors to wrap and highlight
 */
export function initInk(targetSelectors = []) {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Inject required styles
    injectStyles();

    // Wrap elements
    targetSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
            wrapTextNodes(el);
        });
    });

    // Cache spans
    allSpansArray = Array.from(document.querySelectorAll('.custom-char'));
    allSpansArray.forEach((span, i) => {
        span._highlightIdx = i;
    });

    // Zero-Lag Selection Event Listener
    document.addEventListener('selectionchange', () => {
        const selection = window.getSelection();

        // FAST CLEAR
        for (let i = 0; i < prevSelected.length; i++) {
            prevSelected[i].classList.remove('selected');
        }
        prevSelected = [];

        if (selection.isCollapsed || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        let startNode = range.startContainer;
        let endNode = range.endContainer;

        function getSpanIndex(node, isStart) {
            if (node.nodeType === 3 && node.parentNode.classList.contains('custom-char')) {
                return node.parentNode._highlightIdx;
            }
            if (node.nodeType === 1 && node.classList.contains('custom-char')) {
                return node._highlightIdx;
            }
            if (node.nodeType === 1) {
                if (isStart) {
                    const first = node.querySelector('.custom-char');
                    if (first) return first._highlightIdx;
                } else {
                    const all = node.querySelectorAll('.custom-char');
                    if (all.length > 0) return all[all.length - 1]._highlightIdx;
                }
            }
            return null;
        }

        let startIdx = getSpanIndex(startNode, true);
        let endIdx = getSpanIndex(endNode, false);

        // Fallback
        if (startIdx === null || endIdx === null) {
            let ancestor = range.commonAncestorContainer;
            if (ancestor.nodeType === 3) ancestor = ancestor.parentNode;
            const spans = ancestor.querySelectorAll('.custom-char');
            
            let foundStart = false;
            for (let i = 0; i < spans.length; i++) {
                if (selection.containsNode(spans[i], true)) {
                    foundStart = true;
                    spans[i].classList.add('selected');
                    prevSelected.push(spans[i]);
                } else if (foundStart) {
                    break;
                }
            }
            return;
        }

        // Master O(1) Path
        let min = Math.min(startIdx, endIdx);
        let max = Math.max(startIdx, endIdx);

        for (let i = min; i <= max; i++) {
            if (allSpansArray[i]) {
                allSpansArray[i].classList.add('selected');
                prevSelected.push(allSpansArray[i]);
            }
        }
    });
}

// Auto-initialize if loaded via standard script tag with configurations
if (typeof document !== 'undefined') {
    const script = document.currentScript || document.querySelector('script[src*="ink-highlight"]');
    if (script) {
        const targets = script.getAttribute('data-targets');
        if (targets) {
            const color = script.getAttribute('data-ink-color') || '#000000';
            const textColor = script.getAttribute('data-ink-text-color') || '#fffcf2';
            const height = script.getAttribute('data-ink-height') || '0.8em';
            const yOffset = script.getAttribute('data-ink-y-offset') || '50%';
            const xOffset = script.getAttribute('data-ink-x-offset') || '0px';
            const fontFamily = script.getAttribute('data-font-family');

            // Pre-inject configured custom property styles
            const styleId = 'ink-highlight-custom-vars';
            if (!document.getElementById(styleId)) {
                const style = document.createElement('style');
                style.id = styleId;
                
                let styleContent = `
                    :root {
                        --ink-color: ${color};
                        --ink-text-color: ${textColor};
                        --ink-height: ${height};
                        --ink-y-offset: ${yOffset};
                        --ink-x-offset: ${xOffset};
                    }
                `;
                
                // If a specific font family is requested, apply it directly to targeted classes
                if (fontFamily) {
                    styleContent += `
                        ${targets} {
                            font-family: "${fontFamily}", serif !important;
                        }
                    `;
                }

                style.textContent = styleContent;
                document.head.appendChild(style);
            }

            // Run initialization
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => {
                    initInk(targets.split(',').map(s => s.trim()));
                });
            } else {
                initInk(targets.split(',').map(s => s.trim()));
            }
        }
    }
}
